import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { questionnaireApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import PageHeader from '../../components/PageHeader.jsx';
import Card from '../../components/Card.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import StatCard from '../../components/StatCard.jsx';
import Badge from '../../components/Badge.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import { Select, TextInput } from '../../components/Field.jsx';
import { LOCALES } from '../../i18n/index.js';

import fr from '../../i18n/locales/fr.json';
import en from '../../i18n/locales/en.json';
import he from '../../i18n/locales/he.json';
import es from '../../i18n/locales/es.json';

const BUNDLES = { fr, en, he, es };
const CODES = LOCALES.map((locale) => locale.code);
const REFERENCE = 'fr';

/** "a.b.c" -> value, for every leaf in the bundle. */
const flatten = (object, prefix = '') =>
  Object.entries(object).reduce((acc, [key, value]) => {
    const path = `${prefix}${key}`;
    if (value && typeof value === 'object' && !Array.isArray(value)) Object.assign(acc, flatten(value, `${path}.`));
    else acc[path] = value;
    return acc;
  }, {});

/**
 * Translation audit.
 *
 * Two sources of wording, both checked here:
 *   - the interface strings, in frontend/src/i18n/locales/*.json
 *   - the call script, held on the server in the questionnaire definition
 *
 * Read-only on purpose: the files are the source of truth and belong in git.
 * This screen tells you exactly what is missing and where.
 */
const TranslationsPage = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('problems'); // problems | all
  const [locale, setLocale] = useState('');

  const { data: audit } = useApi(() => questionnaireApi.audit(), []);

  const flat = useMemo(
    () => Object.fromEntries(CODES.map((code) => [code, flatten(BUNDLES[code])])),
    [],
  );

  const rows = useMemo(() => {
    const keys = new Set(CODES.flatMap((code) => Object.keys(flat[code])));

    return [...keys].sort().map((key) => {
      const values = Object.fromEntries(CODES.map((code) => [code, flat[code][key]]));
      const missing = CODES.filter((code) => values[code] === undefined || String(values[code]).trim() === '');
      // Same text as French in another language is usually an untranslated copy.
      const untranslated = CODES.filter(
        (code) => code !== REFERENCE && values[code] !== undefined && values[code] === values[REFERENCE],
      );
      return { key, values, missing, untranslated };
    });
  }, [flat]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter === 'problems' && !row.missing.length && !row.untranslated.length) return false;
      if (locale && !row.missing.includes(locale) && !row.untranslated.includes(locale)) return false;
      if (needle) {
        const haystack = `${row.key} ${CODES.map((c) => row.values[c] ?? '').join(' ')}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, search, filter, locale]);

  const missingPerLocale = Object.fromEntries(
    CODES.map((code) => [code, rows.filter((row) => row.missing.includes(code)).length]),
  );
  const untranslatedPerLocale = Object.fromEntries(
    CODES.map((code) => [code, rows.filter((row) => row.untranslated.includes(code)).length]),
  );

  const uiComplete = rows.filter((row) => !row.missing.length).length;

  return (
    <>
      <PageHeader title={t('translations.title')} subtitle={t('translations.subtitle')} />

      <div className="stat-grid">
        <StatCard label={t('translations.totalKeys')} value={rows.length} />
        <StatCard
          label={t('translations.complete')}
          value={`${Math.round((uiComplete / Math.max(1, rows.length)) * 100)}%`}
          tone={uiComplete === rows.length ? 'success' : 'danger'}
        />
        {CODES.map((code) => (
          <StatCard
            key={code}
            label={LOCALES.find((l) => l.code === code)?.nativeLabel ?? code}
            value={missingPerLocale[code] === 0 ? 'OK' : `${missingPerLocale[code]} ${t('translations.missing')}`}
            hint={
              untranslatedPerLocale[code]
                ? `${untranslatedPerLocale[code]} ${t('translations.sameAsFrench')}`
                : undefined
            }
            tone={missingPerLocale[code] === 0 ? 'success' : 'danger'}
          />
        ))}
      </div>

      {/* Server-side wording: the call script lives in the API, not in the bundles. */}
      <Card title={t('translations.questionnaire')}>
        {audit ? (
          <>
            <div className="chip-list">
              {audit.locales.map((code) => (
                <span key={code} className="chip">
                  {code.toUpperCase()}:{' '}
                  {audit.missingPerLocale[code] === 0
                    ? t('translations.ok')
                    : `${audit.missingPerLocale[code]} ${t('translations.missing')}`}
                </span>
              ))}
              <span className="chip">
                {audit.totalTexts} {t('translations.texts')}
              </span>
            </div>
            {audit.incomplete.length ? (
              <ul className="translation-missing">
                {audit.incomplete.slice(0, 40).map((row) => (
                  <li key={row.path}>
                    <code>{row.path}</code> <Badge value="LOST" label={row.missing.join(', ')} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">{t('translations.questionnaireComplete')}</p>
            )}
          </>
        ) : null}
      </Card>

      <Toolbar>
        <TextInput value={search} onChange={setSearch} placeholder={t('common.search')} />
        <div className="segmented" role="group">
          <button
            type="button"
            className={filter === 'problems' ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => setFilter('problems')}
          >
            {t('translations.problemsOnly')}
          </button>
          <button
            type="button"
            className={filter === 'all' ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => setFilter('all')}
          >
            {t('common.all')}
          </button>
        </div>
        <Select
          value={locale}
          onChange={setLocale}
          placeholder={t('common.all')}
          options={LOCALES.map((item) => ({ value: item.code, label: item.nativeLabel }))}
        />
        <span className="toolbar__count">
          {filtered.length} {t('common.results')}
        </span>
      </Toolbar>

      <Card title={t('translations.interface')}>
        {filtered.length ? (
          <div className="table-wrap">
            <table className="table translation-table">
              <thead>
                <tr>
                  <th>{t('translations.key')}</th>
                  {CODES.map((code) => (
                    <th key={code}>{code.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 300).map((row) => (
                  <tr key={row.key}>
                    <td>
                      <code>{row.key}</code>
                    </td>
                    {CODES.map((code) => {
                      const value = row.values[code];
                      const isMissing = row.missing.includes(code);
                      const isCopy = row.untranslated.includes(code);
                      return (
                        <td
                          key={code}
                          className={isMissing ? 'translation-cell--missing' : isCopy ? 'translation-cell--copy' : ''}
                          dir={code === 'he' ? 'rtl' : 'ltr'}
                        >
                          {isMissing ? <em>{t('translations.missing')}</em> : String(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title={t('translations.allGood')} description={t('translations.allGoodHint')} />
        )}
      </Card>
    </>
  );
};

export default TranslationsPage;
